// ─── TwoFactorPage ────────────────────────────────────────────────────────────
import { useState, useRef, useEffect } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '../../modules/auth/api/auth.api'
import { useAuthStore } from '../../stores/auth.store'
import { ROUTES } from '../../router/routes'

export function TwoFactorPage() {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const location = useLocation()
  const state = location.state as { userId?: string } | null
  const { setTokensAndUser } = useAuthStore()

  if (!state?.userId) return <Navigate to={ROUTES.LOGIN} replace />

  const { mutate, isPending } = useMutation({
    mutationFn: (totpCode: string) =>
      authApi.login({
        email: '', // sera résolu par le backend via userId dans le state
        password: '',
        totpCode,
      }),
    onSuccess: (data) => {
      if (!('requires2fa' in data)) {
        setTokensAndUser(data.accessToken, data.refreshToken, data.user as any)
      }
    },
    onError: () => setError('Code invalide. Réessayez.'),
  })

  const handleInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    setError('')

    if (value && index < 5) inputRefs.current[index + 1]?.focus()

    if (newCode.every((d) => d)) {
      mutate(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div className="text-center">
      <div className="w-14 h-14 bg-[#E0F2FE] border border-[rgba(245,124,0,0.25)] rounded-2xl flex items-center justify-center mx-auto mb-6">
        <ShieldCheck className="w-7 h-7 text-[var(--blue-d)]" />
      </div>
      <h2 className="text-2xl font-bold text-[var(--brand-dark)] mb-2">Vérification 2FA</h2>
      <p className="text-[var(--tx3)] mb-8 text-sm">
        Entrez le code à 6 chiffres de votre application authenticator
      </p>

      <div className="flex gap-3 justify-center mb-6">
        {code.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleInput(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="w-12 h-14 text-center text-xl font-bold bg-[var(--surface-2)] border border-[var(--bd)]
                       text-[var(--brand-dark)] rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1
                       focus:ring-[var(--blue)] transition-colors"
          />
        ))}
      </div>

      {error && (
        <div className="flex items-center justify-center gap-2 text-[#DC2626] text-sm mb-4">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {isPending && (
        <div className="flex items-center justify-center gap-2 text-[var(--tx3)] text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Vérification…
        </div>
      )}
    </div>
  )
}

export default TwoFactorPage
